import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function RedbridgeCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Redbridge Pothole Claims | 49% of U-Roads in RED Condition | Fixtyer"
        description="Redbridge Council repaired 63,410 potholes in five years on a 555km network, yet 49% of unclassified roads were in RED condition in 2024/25. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/redbridge"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Redbridge', url: '/councils/redbridge' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Redbridge spends well over triple its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect that damaged your vehicle, not aggregate spending. Redbridge is AMBER overall because nearly half its residential roads are in RED condition and B/C roads saw good-condition share collapse from 33.5% to 20.5% in a single year. Section 58 under the Highways Act depends on whether the council reasonably knew about and repaired that defect — not on whether it outspends its DfT cheque.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 294.3km — 53% of Redbridge\'s 554.9km carriageway network. In 2024/25, 49% of U-roads surveyed were in RED condition, up from 40% the year before. Only 23% were in good (green) condition. If your damage was on a residential street, the council\'s own CVI data is your strongest structural argument.',
          },
          {
            question: 'Why did pothole repairs jump to 21,098 in 2023/24?',
            answer: 'Redbridge introduced an Injection Patcher trial that repaired an additional 2,207 potholes between July and October 2023, on top of its standard inspection programme. That year also saw the borough-wide structural condition survey completed in quarter two of 2023/24. A network producing defects at that rate is one where potholes routinely form between inspections — which is exactly when prior reports and photographic evidence decide claims.',
          },
          {
            question: 'Redbridge has no condition data for 2020/21 through 2022/23 — does that help my claim?',
            answer: 'Potentially, yes. The council\'s own table records "No condition surveys undertaken due to COVID" for 2020/21 and 2021/22, and "No data available for 2022/23." If your incident falls in those years, the council cannot point to network-level condition records to show it knew the state of your road. That weakens any blanket Section 58 defence and makes defect-specific evidence — photos, prior reports, inspection records — decisive.',
          },
          {
            question: 'Only 1,577 potholes were reported but 13,631 were repaired in 2024/25 — what does that mean?',
            answer: 'The council\'s footnote states reported figures are "the number of potholes reported to the Council outside of the highways inspection regime." The 12,054 gap between reported and repaired potholes in 2024/25 shows the inspection programme finds vastly more defects than residents report. For your claim, that cuts both ways: the council clearly runs active inspections, but it also proves the network generates defects faster than the public notices them — making the age and prior history of your specific pothole the battleground.',
          },
          {
            question: 'Does Redbridge\'s AI survey programme strengthen its Section 58 defence?',
            answer: 'It may on paper, but the council itself says the published CVI table covers only the "borough priority network" and that it "will publish more information on its amber and green categories for all of its roads in due course." AI and Vaisala surveys feed resurfacing priorities — they do not automatically prove your specific defect was inspected before it damaged your vehicle. Demand the inspection log for your road, not network-level innovation headlines.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Redbridge Council"
        councilSlug="redbridge"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/redbridge"
        headline="Redbridge Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Redbridge</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Redbridge: 49% of Residential Roads in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            On a 555km network, Redbridge earns a GREEN spend scorecard — investing £3m in resurfacing 
            against a £853,000 DfT allocation. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> because 
            <strong className="text-rose-400"> 49% of unclassified roads were in RED condition</strong> in 2024/25, 
            B/C roads lost a third of their good-condition share in one year, and the council repaired 
            <strong className="text-rose-400"> 63,410 potholes in five years</strong> while publishing 
            no condition surveys at all for three of those years.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">49%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 40% the year before. Only 23% of Redbridge's 294.3km of residential 
              streets were in good condition — on a network where pothole repairs nearly 
              doubled between 2021/22 and 2023/24.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Two years of Vaisala CVI survey data from Redbridge's own transparency report — 
            and three years of blanks
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (43.2km — 7.8% of network): marginal improvement, still a third in RED</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">31%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">down from 33% in 2023/24</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">28%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 30%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">40%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 37%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads ticked up slightly, but 59% of A-roads still need maintenance or will soon. 
              A-roads are less than one-tenth of the borough network.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (217.4km — 39% of network): sharp deterioration</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21 – 2022/23</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No condition surveys undertaken</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">34.7%</td>
                    <td className="px-4 py-2 text-center">31.7%</td>
                    <td className="px-4 py-2 text-center">33.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">39.8%</td>
                    <td className="px-4 py-2 text-center font-bold">39.7%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">20.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Good-condition B/C roads collapsed from 33.5% to 20.5% in one year. Nearly four in 
              ten are now in RED condition, and fewer than one in five are green. That is not 
              gradual decline — it is a step change on the roads most drivers use daily.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (294.3km — 53% of network): nearly half in RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020/21 – 2022/23</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No condition surveys undertaken</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">40%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">49%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              At 49% RED, roughly 144km of residential streets need structural maintenance. 
              Good-condition share fell from 28% to 23% while RED share climbed nine percentage 
              points in twelve months.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The below table reflects the condition of the borough priority network as surveyed by CVI a Course Visual Inspection, which is a rapid, visual assessment of road condition, typically conducted from a slow-moving vehicle, to assess a large part of a road network the Council does annually. The Council are also now using AI surveys to undertake its carriageway assessment and will be moving to the new Department for Transport standard."
              source="Redbridge Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The Council will publish more information on its amber and green categories for all of its roads in due course:"
              source="Redbridge Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£853k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3m</div>
                <div className="text-brand-300 text-sm">Road resurfacing capital 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">65%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Redbridge commits <span className="text-emerald-400">more than triple its DfT allocation</span> to 
              resurfacing alone — plus £2.1m in reactive maintenance — and residential roads are still 
              nearly half RED. The problem is not the budget line. The network is deteriorating faster 
              than even a GREEN-rated spend programme can hold it.
            </p>
          </div>
        </div>
      </section>

      {/* The Survey Gap */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Three Years With No Condition Data
          </h2>
          <p className="text-surface-600 mb-8">
            Redbridge's own CVI table has blanks for half the five-year reporting window
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Condition survey status</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center text-rose-700 font-medium">No condition surveys undertaken due to COVID</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center text-rose-700 font-medium">No condition surveys undertaken due to COVID</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center text-rose-700 font-medium">No data available for 2022/23</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center text-emerald-700">Borough-wide condition survey completed in Q2 2023/24</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-medium">Vaisala CVI annual survey — priority network published</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Priority Network Caveat</h3>
              <p className="text-surface-600 text-sm mb-3">
                Published condition percentages cover the "borough priority network" — not necessarily 
                every metre of the 554.9km carriageway. The council says it will publish amber and 
                green categories for <strong>all</strong> roads "in due course," which means the 
                published RED figures may understate problems on roads outside the priority list.
              </p>
              <p className="text-surface-600 text-sm">
                For Section 58, ask whether your road was on the priority network when the 
                CVI survey passed — and what the inspection record shows for the months between surveys.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI Is Drive-By, Not Boots-on-Ground</h3>
              <p className="text-amber-800 text-sm mb-3">
                Course Visual Inspection is "a rapid, visual assessment of road condition, typically 
                conducted from a slow-moving vehicle." That catches obvious surface defects at speed — 
                it does not replicate the detailed safety inspections that trigger pothole repairs.
              </p>
              <p className="text-amber-800 text-sm">
                A drive-by survey showing 49% RED on U-roads is an admission the residential network 
                is failing. It is not proof every individual pothole was seen and scheduled for repair.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Redbridge must maintain its roads. Under 
              Section 58, it can escape liability only if it proves a reasonable inspection and 
              repair system. For Redbridge's network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road condition-surveyed in the year your pothole formed — or was it a blank year?</li>
              <li>• If 49% of U-roads are RED, what was done about the road you were driving on?</li>
              <li>• Does a drive-by CVI survey count as "reasonable" knowledge of individual defects?</li>
              <li>• Can the council produce the Highways Safety Inspection log for your specific location?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge from surveys it did not conduct — 
              and cannot treat a priority-network snapshot as proof it knew about your pothole.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            63,410 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair on a 555km London borough network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reported*</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">£ spent</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,630</td>
                  <td className="px-6 py-4 text-center">7,597</td>
                  <td className="px-6 py-4 text-center">£403,797</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,233</td>
                  <td className="px-6 py-4 text-center">8,187</td>
                  <td className="px-6 py-4 text-center">£411,574</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,703</td>
                  <td className="px-6 py-4 text-center">12,897</td>
                  <td className="px-6 py-4 text-center">£330,300</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,070</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21,098</td>
                  <td className="px-6 py-4 text-center">£599,990</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,577</td>
                  <td className="px-6 py-4 text-center font-bold">13,631</td>
                  <td className="px-6 py-4 text-center">£791,742</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">8,213</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">63,410</td>
                  <td className="px-6 py-4 text-center font-bold">£2,537,403</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~35 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Redbridge repairs around 35 potholes per day on a network 
                of just 555km. In 2023/24 alone, the Injection Patcher trial added 2,207 repairs 
                between July and October — on top of the standard programme that already produced 
                21,098 filled potholes that year.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Reported-vs-Repaired Gap</h3>
              <p className="text-amber-800 text-sm">
                In 2024/25, residents reported 1,577 potholes outside the inspection regime — but 
                the council repaired 13,631. The injection patcher alone fixed 3,018 additional 
                potholes, and a post-winter find-and-fix programme repaired another 1,723. 
                Inspections find defects the public never reports.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="*This is the number of potholes reported to the Council outside of the highways inspection regime."
            source="Redbridge Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The Injection Patcher Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Patching Faster, Not Fixing Sooner
          </h2>
          <p className="text-surface-600 mb-8">
            Redbridge's innovation programme — what the council says it is doing about defects
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Redbridge Council piloted a Jet Injection Patcher over the 2023 summer period that resulted in the additional repair of 2,202 potholes which proved to be cost effective and reduced carbon output due to not having to excavate and dispose of any material. The Council used the jet patcher again throughout Spring and Summer 2024 year using additional revenue funding provided by the Council to repair an additional 3,018 potholes."
              source="Redbridge Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Redbridge is also working with various organisations that are looking to use AI on the road network to detect potholes and report them through to the maintenance teams to ensure potholes can be picked up and repaired at a faster rate. Redbridge have also commissioned Vaisala Road AI system to conduct annual condition surveys of the roads which feeds into the priority matrix of road resurfacing."
              source="Redbridge Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means For Claims</h3>
              <p className="text-surface-600 text-sm mb-3">
                Injection patching is reactive repair — it fills holes without resurfacing the 
                carriageway. The council's own 2024/25 data shows 3,018 injection-patched potholes 
                plus 1,723 from a post-winter find-and-fix programme, all on top of standard repairs.
              </p>
              <p className="text-surface-600 text-sm">
                Heavy patching activity is evidence the network is producing defects continuously. 
                It does not prove the specific pothole that damaged your vehicle was patched before 
                you hit it — or that patching counted as adequate repair under Section 41.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your pothole injection-patched before you hit it — and did the patch fail?</li>
                <li>• Does the repair record show repeated patching at the same location?</li>
                <li>• If AI detected the defect, when was it reported to maintenance — and when was it scheduled?</li>
                <li>• Was your road on the 15.3km resurfacing programme for 2025/26, or left to patching?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where The Money Goes
          </h2>
          <p className="text-surface-600 mb-8">
            Capital resurfacing, reactive maintenance and preventative share — five years of council figures
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfacing capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive maint.</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£6.75m</td>
                  <td className="px-4 py-3 text-center">£1.47m</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£2.5m</td>
                  <td className="px-4 py-3 text-center">£1.65m</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1.5m</td>
                  <td className="px-4 py-3 text-center">£1.52m</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">50%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£262k</td>
                  <td className="px-4 py-3 text-center">£2.5m</td>
                  <td className="px-4 py-3 text-center">£1.6m</td>
                  <td className="px-4 py-3 text-center">63%</td>
                  <td className="px-4 py-3 text-center">37%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£262k</td>
                  <td className="px-4 py-3 text-center">£3m</td>
                  <td className="px-4 py-3 text-center">£1.5m</td>
                  <td className="px-4 py-3 text-center">69%</td>
                  <td className="px-4 py-3 text-center">31%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center font-bold">£853k</td>
                  <td className="px-4 py-3 text-center font-bold">£3m</td>
                  <td className="px-4 py-3 text-center font-bold">£2.1m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">65%</td>
                  <td className="px-4 py-3 text-center font-bold">35%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-sm italic mb-6">
            Note: the council's published 2022/23 preventative and reactive percentages sum to 110% 
            as printed in the source report.
          </p>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Forward Plan: 15.3km of Resurfacing in 2025/26</h3>
            <p className="text-surface-600 text-sm">
              Redbridge plans approximately 75,000sqm or 15.3km of carriageway resurfacing in 2025/26 — 
              roughly 2.8% of its 554.9km road network. The council assesses needs through its Highways 
              Asset Management Plan and a value-engineering prioritisation matrix. Roads deferred from 
              2024/25 include Ley Street (Ilford Town) and Mallards Road (Churchfields), the latter 
              "deferred indefinitely due to Broadmead Bridge."
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Redbridge invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — resurfacing capital dwarfs DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Active inspection regime finding 7–8× more potholes than residents report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Innovation investment — injection patcher, AI surveys, Vaisala CVI
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Highways Inspection Strategy and Asset Management Plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road green-condition share improving (37% → 40%)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-inspected classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 49% of U-roads in RED condition — nearly half of all residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C good-condition share collapsed from 33.5% to 20.5% in one year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No condition surveys for 2020/21, 2021/22 or 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Published data covers "priority network" only — full network unpublished
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 63,410 pothole repairs in five years — defects outpace resurfacing (15.3km planned)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — gaps in published condition coverage
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and an active inspection programme, your claim lives 
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, failed injection patches)</li>
              <li>• The road's class — on a U-road, the 49% RED figure is your strongest structural argument</li>
              <li>• Whether your incident falls in a year with no published condition survey data</li>
              <li>• The council's repair record for that exact location — patching history proves knowledge</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Redbridge's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Redbridge?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 49% RED argument</li>
                <li>• No prior-report search</li>
                <li>• No inspection-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 49% U-road RED condition documented</li>
                <li>✅ Three-year survey gap argued</li>
                <li>✅ 63,410 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Redbridge</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=redbridge"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Redbridge Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Redbridge spends well over triple its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect that 
                damaged your vehicle, not aggregate spending. Redbridge is AMBER overall because nearly 
                half its residential roads are in RED condition and B/C roads saw good-condition share 
                collapse from 33.5% to 20.5% in a single year. Section 58 under the Highways Act depends 
                on whether the council reasonably knew about and repaired that defect — not on whether 
                it outspends its DfT cheque.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 294.3km — 53% of Redbridge's 554.9km carriageway network. In 2024/25, 
                49% of U-roads surveyed were in RED condition, up from 40% the year before. Only 23% were 
                in good (green) condition. If your damage was on a residential street, the council's own 
                CVI data is your strongest structural argument.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs jump to 21,098 in 2023/24?
              </h3>
              <p className="text-surface-600 text-sm">
                Redbridge introduced an Injection Patcher trial that repaired an additional 2,207 potholes 
                between July and October 2023, on top of its standard inspection programme. That year also 
                saw the borough-wide structural condition survey completed in quarter two of 2023/24. A network 
                producing defects at that rate is one where potholes routinely form between inspections — which 
                is exactly when prior reports and photographic evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Redbridge has no condition data for 2020/21 through 2022/23 — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, yes. The council's own table records "No condition surveys undertaken due to COVID" 
                for 2020/21 and 2021/22, and "No data available for 2022/23." If your incident falls in those 
                years, the council cannot point to network-level condition records to show it knew the state of 
                your road. That weakens any blanket Section 58 defence and makes defect-specific evidence — 
                photos, prior reports, inspection records — decisive.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Only 1,577 potholes were reported but 13,631 were repaired in 2024/25 — what does that mean?
              </h3>
              <p className="text-surface-600 text-sm">
                The council's footnote states reported figures are "the number of potholes reported to the 
                Council outside of the highways inspection regime." The 12,054 gap between reported and repaired 
                potholes in 2024/25 shows the inspection programme finds vastly more defects than residents 
                report. For your claim, that cuts both ways: the council clearly runs active inspections, but 
                it also proves the network generates defects faster than the public notices them — making the 
                age and prior history of your specific pothole the battleground.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Redbridge's AI survey programme strengthen its Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It may on paper, but the council itself says the published CVI table covers only the "borough 
                priority network" and that it "will publish more information on its amber and green categories 
                for all of its roads in due course." AI and Vaisala surveys feed resurfacing priorities — they 
                do not automatically prove your specific defect was inspected before it damaged your vehicle. 
                Demand the inspection log for your road, not network-level innovation headlines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.redbridge.gov.uk/roads-and-pavements/highways-maintenance-and-resurfacing-information/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Redbridge Council — Highways Maintenance and Resurfacing Information (2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
